import type { components } from '@/api/schema'
import type { AiComponentType } from './ai-component-library/ai-components-types'

export type JsonObject = Record<string, unknown>

const SUPPORTED_AI_COMPONENT_TYPES = new Set<AiComponentType>([
  'ai-table',
  'ai-bar-chart',
  'ai-line-chart',
  'ai-radial-bar-chart',
  'ai-area-chart',
  'ai-pie-chart',
  'ai-radar-chart',
])

export interface ChatCatalogRequest extends JsonObject {
  componentType?: string
  handlerId?: string
  endpoint?: string
  filters?: Record<string, unknown>[]
  payload?: JsonObject
}

export interface ChatCatalogComponent {
  componentType: string
  uiSchema?: JsonObject
  dataSchema?: JsonObject
  metadata?: JsonObject
  capturedAt?: string
}

export interface ChatAiComponent {
  componentType: AiComponentType
  schema: JsonObject & { componentType: AiComponentType }
  metadata?: JsonObject
}

/**
 * Response format for chat messages from the Python backend.
 * Note: Backend uses snake_case (by_alias=False in Pydantic model_dump)
 */
export interface ChatResponse {
  response?: string | null
  thinking?: string | null
  error?: string | null
  done: boolean
  cancelled?: boolean | null
  /** One-shot envelope: worker joined the chat channel and cancel is safe */
  started?: boolean | null
  requestId?: string | null
  request_id?: string | null
  total_duration?: number | null
  totalDuration?: number | null

  // Agent step fields for streaming agent actions
  stepType?: string | null
  stepNumber?: number | null
  toolName?: string | null
  toolArgs?: Record<string, unknown> | null
  toolResult?: string | null
  step_type?: string | null
  step_number?: number | null
  tool_name?: string | null
  tool_args?: Record<string, unknown> | null
  tool_result?: string | null

  catalogComponents?: unknown[] | null
  catalog_components?: unknown[] | null
  catalogRequests?: unknown[] | null
  catalog_requests?: unknown[] | null
  aiComponents?: unknown[] | null
  ai_components?: unknown[] | null
  blocks?: unknown[] | null

  contextEvent?: ChatContextEventName | null
  context_event?: ChatContextEventName | null
  contextState?: ServerContextState | null
  context_state?: ServerContextState | null
}

/** Worker-ready marker (not a chat bubble). See live-chat docs agent-system/02-frontend.md */
export function isChatStartedEnvelope(response: ChatResponse): boolean {
  return Boolean(response.started) && !response.done && !response.error
}

export function getChatResponseRequestId(response: ChatResponse): string | undefined {
  const requestId = response.requestId ?? response.request_id
  return typeof requestId === 'string' && requestId.trim().length > 0 ? requestId : undefined
}

export function getContextEventFromResponse(response: ChatResponse): ChatContextEventName | undefined {
  const event = response.contextEvent ?? response.context_event
  if (
    event === 'loaded' ||
    event === 'compacting' ||
    event === 'compacted' ||
    event === 'skipped' ||
    event === 'post_turn'
  ) {
    return event
  }
  return undefined
}

function normalizeUsageSnapshot(raw: ServerUsageSnapshot | null | undefined): ChatContextUsageSnapshot | undefined {
  if (!raw) return undefined

  const promptTokens = raw.promptTokens ?? (raw as { prompt_tokens?: number }).prompt_tokens
  const completionTokens = raw.completionTokens ?? (raw as { completion_tokens?: number }).completion_tokens
  const totalTokens = raw.totalTokens ?? (raw as { total_tokens?: number }).total_tokens
  const model = raw.model

  if (
    typeof promptTokens !== 'number' ||
    typeof completionTokens !== 'number' ||
    typeof totalTokens !== 'number' ||
    typeof model !== 'string'
  ) {
    return undefined
  }

  return {
    promptTokens,
    completionTokens,
    totalTokens,
    model,
  }
}

export function normalizeContextState(raw: ServerContextState | null | undefined): ChatContextState | undefined {
  if (!raw) return undefined

  const estimatedTokens = raw.estimatedTokens ?? (raw as { estimated_tokens?: number }).estimated_tokens
  const contextWindow = raw.contextWindow ?? (raw as { context_window?: number }).context_window
  const hotMessageCount = raw.hotMessageCount ?? (raw as { hot_message_count?: number }).hot_message_count
  const hasWarmSummary = raw.hasWarmSummary ?? (raw as { has_warm_summary?: boolean }).has_warm_summary

  if (
    typeof estimatedTokens !== 'number' ||
    typeof contextWindow !== 'number' ||
    typeof hotMessageCount !== 'number' ||
    typeof hasWarmSummary !== 'boolean'
  ) {
    return undefined
  }

  return {
    estimatedTokens,
    contextWindow,
    hotMessageCount,
    hasWarmSummary,
    lastCompactionAtMs: raw.lastCompactionAtMs ?? (raw as { last_compaction_at_ms?: number | null }).last_compaction_at_ms,
    lastCompactionSummarised:
      raw.lastCompactionSummarised ?? (raw as { last_compaction_summarised?: number | null }).last_compaction_summarised,
    usageLastTurn:
      normalizeUsageSnapshot(raw.usageLastTurn ?? (raw as { usage_last_turn?: ServerUsageSnapshot | null }).usage_last_turn) ??
      null,
  }
}

export function getContextStateFromResponse(response: ChatResponse): ChatContextState | undefined {
  return normalizeContextState(response.contextState ?? response.context_state)
}

export function getContextUsagePercent(state: ChatContextState): number | null {
  if (state.contextWindow <= 0) return null
  return Math.min(100, Math.round((state.estimatedTokens / state.contextWindow) * 100))
}

/**
 * Agent step for tool calls during message processing
 */
export interface AgentStep {
  stepNumber: number
  stepType: string
  toolName?: string
  toolArgs?: Record<string, unknown>
  toolResult?: string
}

/**
 * Chat message stored in frontend state/localStorage
 */
export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  thinking?: string
  cancelled?: boolean
  isStreaming?: boolean
  steps?: AgentStep[]
  catalogComponents?: ChatCatalogComponent[]
  catalogRequests?: ChatCatalogRequest[]
  aiComponents?: ChatAiComponent[]
  timestamp: number
}

/**
 * Chat session stored in localStorage
 */
export interface ChatSession {
  chatId: string
  messages: ChatMessage[]
  createdAt: number
  lastUpdatedAt: number
}

/**
 * Saved chat metadata for the chat history list
 */
export interface SavedChatMeta {
  /** Unique chat ID */
  id: string
  /** Auto-generated or user-defined title */
  title: string
  /** First message preview */
  preview: string
  /** Bot type used in this chat */
  botType: string
  /** Number of messages in the chat */
  messageCount: number
  /** Creation timestamp */
  createdAt: number
  /** Last update timestamp */
  lastUpdatedAt: number
}

/**
 * Chat history stored in localStorage - contains metadata for all saved chats
 */
export interface ChatHistoryStore {
  /** Currently active chat ID (null for new unsaved chat) */
  activeChatId: string | null
  /** List of saved chat metadata */
  chats: SavedChatMeta[]
}

export type ServerSavedSessionMeta = components['schemas']['Messages.ChatHistory.SavedSessionMeta']
export type ServerChatSession = components['schemas']['Messages.ChatHistory.ChatSession']
export type ServerChatMessage = components['schemas']['Messages.ChatHistory.ChatMessage']
export type ServerChatCatalogComponent = components['schemas']['Messages.ChatHistory.CatalogComponentPayload']
export type ServerContextState = components['schemas']['Messages.ChatHistory.ContextState']
export type ServerUsageSnapshot = components['schemas']['Messages.ChatHistory.UsageSnapshot']

export type ChatContextEventName = 'loaded' | 'compacting' | 'compacted' | 'skipped' | 'post_turn'

export type ChatContextUsageSnapshot = {
  promptTokens: number
  completionTokens: number
  totalTokens: number
  model: string
}

/** Normalised context window telemetry pushed over SignalR and persisted on ChatSession. */
export type ChatContextState = {
  estimatedTokens: number
  contextWindow: number
  hotMessageCount: number
  hasWarmSummary: boolean
  lastCompactionAtMs?: number | null
  lastCompactionSummarised?: number | null
  usageLastTurn?: ChatContextUsageSnapshot | null
}

type ServerChatMessageWithAiComponents = ServerChatMessage & {
  aiComponents?: unknown[] | null
  ai_components?: unknown[] | null
}

function toSafeTimestamp(value: number | undefined | null): number {
  return typeof value === 'number' ? value : Date.now()
}

function toRecord(value: unknown): JsonObject | undefined {
  return value !== null && typeof value === 'object' ? (value as JsonObject) : undefined
}

function toRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => entry !== null && typeof entry === 'object')
    : []
}

function getString(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' && value.trim().length > 0) {
      return value
    }
  }
  return undefined
}

function toSafeRole(role: string | undefined | null): ChatMessage['role'] {
  return role === 'user' ? 'user' : 'assistant'
}

function serverMessageRoleLower(m: ServerChatMessage): string {
  return (m.role ?? '').toLowerCase()
}

/**
 * Server blobs store the full LLM transcript: an assistant message with
 * `toolCalls`, each `tool` role message with results, then the final assistant
 * (with `thinking`, `steps`, `aiComponents`). The chat UI only supports
 * `user` | `assistant` and expects a single assistant bubble per turn — tool
 * rows must not be mapped to assistant (that duplicated huge "content").
 *
 * Keep the last `assistant` in each non-user run segment; drop intermediate
 * assistant/tool rows. The final assistant already carries merged `steps`.
 */
function collapsePersistedLlmMessagesForUi(messages: ServerChatMessage[]): ServerChatMessage[] {
  const out: ServerChatMessage[] = []
  let i = 0
  while (i < messages.length) {
    const r = serverMessageRoleLower(messages[i]!)
    if (r === 'user') {
      out.push(messages[i]!)
      i += 1
      continue
    }
    let j = i
    let lastAssistant: ServerChatMessage | null = null
    while (j < messages.length && serverMessageRoleLower(messages[j]!) !== 'user') {
      if (serverMessageRoleLower(messages[j]!) === 'assistant') {
        lastAssistant = messages[j]!
      }
      j += 1
    }
    if (lastAssistant !== null) {
      out.push(lastAssistant)
    }
    i = j
  }
  return out
}

function mapServerChatMessageToChatMessage(message: ServerChatMessage): ChatMessage {
  const messageWithAiComponents = message as ServerChatMessageWithAiComponents

  return {
    id: message.id ?? crypto.randomUUID(),
    role: toSafeRole(message.role),
    content: message.content ?? '',
    thinking: message.thinking ?? undefined,
    cancelled: Boolean(message.cancelled),
    isStreaming: Boolean(message.isStreaming),
    steps: toAgentSteps(message.steps),
    catalogComponents: normalizeCatalogComponents(
      (message as ServerChatMessage & { catalogComponents?: ServerChatCatalogComponent[] | null }).catalogComponents
    ),
    catalogRequests: normalizeCatalogRequests(
      (
        message as ServerChatMessage & {
          catalogRequests?: unknown[] | null
          catalog_requests?: unknown[] | null
        }
      ).catalogRequests ??
        (
          message as ServerChatMessage & {
            catalogRequests?: unknown[] | null
            catalog_requests?: unknown[] | null
          }
        ).catalog_requests
    ),
    aiComponents: normalizeAiComponents(messageWithAiComponents.aiComponents ?? messageWithAiComponents.ai_components),
    timestamp: toSafeTimestamp(message.timestamp),
  }
}

function getCapturedAt(metadata: JsonObject | undefined): string | undefined {
  if (!metadata) return undefined

  return getString(metadata, ['capturedAt', 'captured_at', 'timestamp'])
}

function toSupportedAiComponentType(value: unknown): AiComponentType | undefined {
  return typeof value === 'string' && SUPPORTED_AI_COMPONENT_TYPES.has(value as AiComponentType)
    ? (value as AiComponentType)
    : undefined
}

function stableJsonStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableJsonStringify(entry)).join(',')}]`
  }

  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJsonStringify(record[key])}`)
      .join(',')}}`
  }

  return JSON.stringify(value) ?? 'undefined'
}

function getAiComponentIdentity(component: ChatAiComponent): string {
  return `${component.componentType}:${stableJsonStringify(component.schema)}`
}

export function normalizeAiComponentPayload(component: unknown): ChatAiComponent | null {
  const componentRecord = toRecord(component)
  if (!componentRecord) return null

  const schema = toRecord(componentRecord.schema_data ?? componentRecord.schema)
  if (!schema) return null

  const componentType =
    toSupportedAiComponentType(schema.componentType ?? schema.component_type) ??
    toSupportedAiComponentType(componentRecord.componentType ?? componentRecord.component_type)
  if (!componentType) return null

  return {
    componentType,
    schema: {
      ...schema,
      componentType,
    },
    metadata: toRecord(componentRecord.metadata),
  }
}

export function normalizeAiComponents(value: unknown): ChatAiComponent[] {
  if (!Array.isArray(value)) return []

  const normalizedComponents = value
    .map((component) => normalizeAiComponentPayload(component))
    .filter((component): component is ChatAiComponent => component !== null)

  return mergeAiComponents([], normalizedComponents)
}

export function mergeAiComponents(
  existingComponents: ChatAiComponent[],
  nextComponents: ChatAiComponent[]
): ChatAiComponent[] {
  const seen = new Set<string>()
  return [...existingComponents, ...nextComponents].filter((component) => {
    const identity = getAiComponentIdentity(component)
    if (seen.has(identity)) return false

    seen.add(identity)
    return true
  })
}

export function normalizeCatalogComponentPayload(component: unknown): ChatCatalogComponent | null {
  const componentRecord = toRecord(component)
  if (!componentRecord) return null

  const componentType = getString(componentRecord, ['componentType', 'component_type'])
  if (!componentType) return null

  const uiSchema = toRecord(componentRecord.uiSchema ?? componentRecord.ui_schema)
  const dataSchema = toRecord(componentRecord.dataSchema ?? componentRecord.data_schema)
  const metadata = toRecord(componentRecord.metadata)

  return {
    componentType,
    uiSchema,
    dataSchema,
    metadata,
    capturedAt: getCapturedAt(metadata),
  }
}

export function normalizeCatalogComponents(value: unknown): ChatCatalogComponent[] {
  if (!Array.isArray(value)) return []

  return value
    .map((component) => normalizeCatalogComponentPayload(component))
    .filter((component): component is ChatCatalogComponent => component !== null)
}

export function normalizeCatalogRequestPayload(request: unknown): ChatCatalogRequest | null {
  const requestRecord = toRecord(request)
  if (!requestRecord) return null

  const componentType = getString(requestRecord, ['componentType', 'component_type'])
  const handlerId = getString(requestRecord, ['handlerId', 'handler_id', 'contentId', 'content_id'])
  const endpoint = getString(requestRecord, ['endpoint'])
  const filters = toRecordArray(requestRecord.filters)

  if (!componentType) return null

  return {
    ...requestRecord,
    componentType,
    handlerId,
    endpoint,
    filters,
  }
}

export function normalizeCatalogRequests(value: unknown): ChatCatalogRequest[] {
  if (!Array.isArray(value)) return []

  return value
    .map((request) => normalizeCatalogRequestPayload(request))
    .filter((request): request is ChatCatalogRequest => request !== null)
}

export function getCatalogComponentsFromResponse(response: ChatResponse): ChatCatalogComponent[] {
  const normalizedCatalogComponents = normalizeCatalogComponents(response.catalog_components ?? response.catalogComponents)
  if (normalizedCatalogComponents.length > 0) {
    return normalizedCatalogComponents
  }

  return normalizeCatalogComponents(response.blocks)
}

export function getCatalogRequestsFromResponse(response: ChatResponse): ChatCatalogRequest[] {
  return normalizeCatalogRequests(response.catalog_requests ?? response.catalogRequests)
}

export function getAiComponentsFromResponse(response: ChatResponse): ChatAiComponent[] {
  return normalizeAiComponents(response.ai_components ?? response.aiComponents)
}

function toAgentSteps(steps: unknown[] | null | undefined): AgentStep[] {
  if (!Array.isArray(steps)) return []

  return steps
    .map((step, index): AgentStep | null => {
      if (!step || typeof step !== 'object') return null
      const stepRecord = step as Record<string, unknown>
      return {
        stepNumber:
          typeof stepRecord.stepNumber === 'number'
            ? stepRecord.stepNumber
            : typeof stepRecord.step_number === 'number'
              ? stepRecord.step_number
              : index + 1,
        stepType:
          typeof stepRecord.stepType === 'string'
            ? stepRecord.stepType
            : typeof stepRecord.step_type === 'string'
              ? stepRecord.step_type
              : 'tool',
        toolName:
          typeof stepRecord.toolName === 'string'
            ? stepRecord.toolName
            : typeof stepRecord.tool_name === 'string'
              ? stepRecord.tool_name
              : undefined,
        toolArgs:
          typeof stepRecord.toolArgs === 'object'
            ? (stepRecord.toolArgs as Record<string, unknown>)
            : typeof stepRecord.tool_args === 'object'
              ? (stepRecord.tool_args as Record<string, unknown>)
              : undefined,
        toolResult:
          typeof stepRecord.toolResult === 'string'
            ? stepRecord.toolResult
            : typeof stepRecord.tool_result === 'string'
              ? stepRecord.tool_result
              : undefined,
      }
    })
    .filter((step): step is AgentStep => step !== null)
}

export function mapSavedSessionMetaToSavedChatMeta(meta: ServerSavedSessionMeta): SavedChatMeta {
  return {
    id: meta.chatHistoryId ?? '',
    title: meta.title?.trim() || 'New Chat',
    preview: meta.preview?.trim() || 'No response yet',
    botType: meta.agentKey?.trim() || 'plain',
    messageCount: meta.messageCount ?? 0,
    createdAt: toSafeTimestamp(meta.createdAt),
    lastUpdatedAt: toSafeTimestamp(meta.lastUpdatedAt),
  }
}

export function mapServerChatSessionToChatSession(session: ServerChatSession, fallbackChatId: string): ChatSession {
  const now = Date.now()
  const resolvedChatId = session.chatHistoryId ?? fallbackChatId
  const serverMessages = session.messages ?? []
  const forUi = collapsePersistedLlmMessagesForUi(serverMessages)

  return {
    chatId: resolvedChatId,
    messages: forUi.map(mapServerChatMessageToChatMessage),
    createdAt: toSafeTimestamp(session.createdAt ?? now),
    lastUpdatedAt: toSafeTimestamp(session.lastUpdatedAt ?? now),
  }
}
